import { FeatherChevronDown } from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import React, { useMemo, useState } from "react";
import {
  getEffectiveMonth,
  getEffectiveYear,
} from "../../../../../utils/effectiveDate";
import { Button } from "../../../../../ui/components/Button";
import { DropdownMenu } from "../../../../../ui/components/DropdownMenu";
import { WeeklyCashMovementChart } from "./WeeklyCashMovementChart";
import { WeeklyCashMovementInsightsPanel } from "./WeeklyCashMovementInsightsPanel";
import { useWeeklyCashFlow } from "../../hooks/useWeeklyCashFlow";
import { buildWeeklyCashMovementMonthOptions } from "../../utils/weeklyCashMovementMonthOptions";

function WeeklyCashMovementSectionSkeleton() {
  return (
    <div className="flex w-full flex-col items-start gap-6 rounded-lg bg-default-background px-8 py-8 shadow-md">
      {/* Header skeleton */}
      <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col items-start gap-2">
          <div className="h-7 w-52 animate-pulse rounded bg-neutral-200" />
          <div className="h-4 w-40 animate-pulse rounded bg-neutral-100" />
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className="h-8 w-36 animate-pulse rounded-md bg-neutral-200" />
          <div className="flex items-center gap-2 rounded-full border border-solid border-neutral-200 bg-neutral-50 px-4 py-1.5">
            <div className="h-4 w-14 animate-pulse rounded bg-neutral-200" />
            <div className="h-4 w-10 animate-pulse rounded bg-neutral-200" />
          </div>
        </div>
      </div>
      {/* Chart + Insights row skeleton */}
      <div className="flex w-full gap-6 items-stretch min-h-[320px]">
        {/* Chart skeleton: legend + bars + week labels */}
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6 rounded-lg border border-solid border-neutral-border bg-default-background px-6 py-6">
          <div className="flex w-full items-center gap-6">
            <div className="h-4 w-20 animate-pulse rounded bg-neutral-200" />
            <div className="h-4 w-20 animate-pulse rounded bg-neutral-200" />
          </div>
          <div className="flex w-full items-end gap-4">
            <div className="flex flex-col items-end justify-between py-1 h-64 gap-10">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-4 w-10 animate-pulse rounded bg-neutral-100"
                />
              ))}
            </div>
            <div className="flex flex-1 flex-col gap-3">
              <div className="flex h-64 w-full items-end justify-around gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="flex flex-1 min-w-0 max-w-12 flex gap-1 items-end justify-center"
                    style={{ height: 256 }}
                  >
                    <div
                      className="flex-1 rounded-t-sm bg-neutral-200 animate-pulse"
                      style={{
                        height: `${60 + (i % 3) * 50}px`,
                      }}
                    />
                    <div
                      className="flex-1 rounded-t-sm bg-neutral-200 animate-pulse"
                      style={{
                        height: `${40 + (i % 2) * 60}px`,
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="flex w-full items-center justify-around gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="h-4 w-12 animate-pulse rounded bg-neutral-100"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* Insights panel skeleton — stretch to chart row height */}
        <div className="flex h-full w-full max-w-sm shrink-0 self-stretch min-h-0 flex-col">
          <div className="flex h-full min-h-0 w-full flex-col gap-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex min-h-0 w-full flex-1 flex-col gap-3 rounded-lg border-2 border-solid border-neutral-border bg-default-background px-4 py-4 border-l-neutral-300"
              >
                <div className="h-3 w-16 animate-pulse rounded bg-neutral-200" />
                <div
                  className="h-5 w-full max-w-[200px] animate-pulse rounded bg-neutral-200"
                  style={{ width: `${70 + i * 10}%` }}
                />
                <div
                  className="h-4 w-full animate-pulse rounded bg-neutral-100"
                  style={{ width: `${85 + (i % 2) * 10}%` }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function WeeklyCashMovementSection() {
  const monthOptions = useMemo(() => buildWeeklyCashMovementMonthOptions(), []);
  const defaultValue = monthOptions[0]?.value ?? "";
  const [selectedValue, setSelectedValue] = useState(defaultValue);

  const selectedOption =
    monthOptions.find((o) => o.value === selectedValue) ?? monthOptions[0];

  const apiParams =
    selectedOption != null
      ? { year: selectedOption.year, month: selectedOption.month }
      : undefined;

  const { weeks, insights, monthLabel, mtdNet, loading, error } =
    useWeeklyCashFlow(apiParams);

  const isViewingEffectiveMonth =
    selectedOption != null &&
    selectedOption.year === getEffectiveYear() &&
    selectedOption.month === getEffectiveMonth();

  const netLabel = isViewingEffectiveMonth ? "MTD net" : "Month net";

  const trimmedMtdNet = mtdNet.trim();
  const mtdNetPillBorderBg = !trimmedMtdNet
    ? "border-neutral-200 bg-neutral-50"
    : trimmedMtdNet.startsWith("-")
      ? "border-error-100 bg-error-50"
      : "border-success-100 bg-success-50";
  const mtdNetValueClass = !trimmedMtdNet
    ? "text-subtext-color"
    : trimmedMtdNet.startsWith("-")
      ? "text-error-600"
      : "text-success-600";

  const showFullSkeleton = loading && weeks.length === 0;

  if (showFullSkeleton) {
    return <WeeklyCashMovementSectionSkeleton />;
  }

  return (
    <div className="flex w-full flex-col items-start gap-6 rounded-lg bg-default-background px-8 py-8 shadow-md">
      <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col items-start gap-1 min-w-0">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Weekly Cash Movement
          </span>
          <span className="text-caption font-caption text-subtext-color">
            {monthLabel
              ? `Weekly comparison · ${monthLabel}`
              : "Weekly comparison"}
          </span>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <SubframeCore.DropdownMenu.Root>
            <SubframeCore.DropdownMenu.Trigger asChild={true}>
              <Button
                variant="neutral-secondary"
                size="medium"
                disabled={loading}
                iconRight={<FeatherChevronDown />}
                aria-label={`Month, ${selectedOption?.label ?? "select"}`}
                onClick={() => {}}
              >
                {selectedOption?.label ?? "Select month"}
              </Button>
            </SubframeCore.DropdownMenu.Trigger>
            <SubframeCore.DropdownMenu.Portal>
              <SubframeCore.DropdownMenu.Content
                side="bottom"
                align="end"
                sideOffset={4}
                asChild={true}
              >
                <DropdownMenu maxHeight="max-h-72">
                  {monthOptions.map((o) => (
                    <DropdownMenu.DropdownItem
                      key={o.value}
                      icon={null}
                      className={selectedValue === o.value ? "bg-brand-50" : ""}
                      onClick={() => setSelectedValue(o.value)}
                    >
                      {o.label}
                    </DropdownMenu.DropdownItem>
                  ))}
                </DropdownMenu>
              </SubframeCore.DropdownMenu.Content>
            </SubframeCore.DropdownMenu.Portal>
          </SubframeCore.DropdownMenu.Root>
          <div
            className={`flex items-center gap-2 rounded-full border border-solid px-4 py-1.5 ${mtdNetPillBorderBg}`}
          >
            <span className="text-caption font-caption text-subtext-color">
              {netLabel}
            </span>
            <span
              className={`text-caption-bold font-caption-bold ${mtdNetValueClass}`}
            >
              {mtdNet || "—"}
            </span>
          </div>
        </div>
      </div>
      {error && (
        <span className="text-caption font-caption text-error-600">
          {error}
        </span>
      )}
      <div
        className={`flex w-full gap-6 items-stretch min-h-0 ${loading ? "opacity-60 pointer-events-none" : ""}`}
      >
        <div className="flex min-w-0 grow-[1.25]">
          <WeeklyCashMovementChart weeks={weeks} loading={false} />
        </div>
        <div className="flex h-full w-full max-w-[300px] shrink-0 self-stretch min-h-0 flex-col">
          <WeeklyCashMovementInsightsPanel
            insights={insights}
            loading={false}
          />
        </div>
      </div>
    </div>
  );
}
